import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as HealthyIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  PlayArrow as RunningIcon,
  Stop as StoppedIcon,
  Schedule as ScheduleIcon,
  Speed as FrequencyIcon,
  TrendingUp as UptimeIcon,
  Storage as ProcessedIcon,
  Settings as ConfigIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';

interface Carrier {
  id: string;
  name: string;
  carrier_type: string;
  status: 'healthy' | 'warning' | 'error' | 'disabled';
  status_color: 'success' | 'warning' | 'error' | 'default';
  is_enabled: boolean;
  is_running: boolean;
  connection_type: string;
  connection_details: string;
  configured_interval: number;
  actual_interval?: number;
  uptime_percentage: number;
  uptime_timeframe_hours: number;
  last_successful_connection: string | null;
  next_scheduled_check: string | null;
  outages_processed?: number | null;
  outages_label?: string;
  last_error: string | null;
  last_success_label?: string;
  created_at: string | null;
  updated_at: string | null;
  total_emails_processed?: number;
  last_email_processed_at?: string | null;
}

interface CarrierStatusData {
  carriers: Carrier[];
  case_mailbox_poller?: Carrier | null;
  support_desk_mailbox_poller?: Carrier | null;
  total_carriers: number;
  healthy_carriers: number;
  warning_carriers: number;
  error_carriers: number;
  disabled_carriers: number;
  timestamp: string;
}

const CarrierStatusPage: React.FC = () => {
  const [data, setData] = useState<CarrierStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState(24);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchCarrierStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getCarrierStatus(timeframe);
      setData(result);
    } catch (err: any) {
      setError('Failed to load feed status');
      console.error('Error fetching feed status:', err);
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => {
    fetchCarrierStatus();
  }, [fetchCarrierStatus]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchCarrierStatus();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, fetchCarrierStatus]);

  const getStatusIcon = (status: string, size: 'small' | 'medium' = 'medium') => {
    switch (status) {
      case 'healthy':
        return <HealthyIcon color="success" fontSize={size} />;
      case 'warning':
        return <WarningIcon color="warning" fontSize={size} />;
      case 'error':
        return <ErrorIcon color="error" fontSize={size} />;
      case 'disabled':
        return <StoppedIcon color="disabled" fontSize={size} />;
      default:
        return <ConfigIcon color="disabled" fontSize={size} />;
    }
  };

  const formatInterval = (seconds: number | undefined | null) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'PPp');
    } catch {
      return dateString;
    }
  };

  if (loading && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Feed Status
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading feed status...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Feed Status
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchCarrierStatus}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Feed Status
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={timeframe}
              label="Timeframe"
              onChange={(e) => setTimeframe(Number(e.target.value))}
            >
              <MenuItem value={1}>1 Hour</MenuItem>
              <MenuItem value={6}>6 Hours</MenuItem>
              <MenuItem value={24}>24 Hours</MenuItem>
              <MenuItem value={168}>7 Days</MenuItem>
              <MenuItem value={720}>30 Days</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title={autoRefresh ? 'Auto-refresh enabled (30s)' : 'Auto-refresh disabled'}>
            <Button
              variant={autoRefresh ? 'contained' : 'outlined'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              size="small"
            >
              Auto
            </Button>
          </Tooltip>
          
          <Tooltip title="Refresh now">
            <IconButton onClick={fetchCarrierStatus} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary Cards */}
      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="text.primary">
                    {data.total_carriers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Carriers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="success.main">
                    {data.healthy_carriers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Healthy
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="warning.main">
                    {data.warning_carriers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Warning
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="error.main">
                    {data.error_carriers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Error
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="text.disabled">
                    {data.disabled_carriers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Disabled
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Carriers, case mailbox, support desk mailbox */}
          <Grid container spacing={3}>
            {[...data.carriers, ...(data.case_mailbox_poller ? [data.case_mailbox_poller] : []), ...(data.support_desk_mailbox_poller ? [data.support_desk_mailbox_poller] : [])].map((carrier) => (
              <Grid item xs={12} lg={6} xl={4} key={carrier.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    border: `2px solid`,
                    borderColor: carrier.status === 'healthy' ? 'success.light' : 
                                carrier.status === 'warning' ? 'warning.light' :
                                carrier.status === 'error' ? 'error.light' : 'grey.300'
                  }}
                >
                  <CardContent>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" component="div">
                            {carrier.name}
                          </Typography>
                          {(carrier.id === 'case-mailbox-poller' || carrier.id === 'support-desk-mailbox-poller') && (
                            <Tooltip title="Configure in Settings → Case Module → Mailboxes">
                              <IconButton component={Link} to="/settings" size="small" sx={{ p: 0.5 }}>
                                <ConfigIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {carrier.carrier_type.toUpperCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {getStatusIcon(carrier.status)}
                        <Chip
                          size="small"
                          icon={carrier.is_running ? <RunningIcon /> : <StoppedIcon />}
                          label={carrier.is_running ? 'Running' : 'Stopped'}
                          color={carrier.is_running ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    {/* Connection Info */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Connection
                      </Typography>
                      <Typography variant="body2">
                        {carrier.connection_type}: {carrier.connection_details}
                      </Typography>
                    </Box>

                    {/* Status Grid */}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <UptimeIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">
                            Uptime
                          </Typography>
                        </Box>
                        <Typography variant="h6" color={carrier.uptime_percentage >= 95 ? 'success.main' : 
                                                      carrier.uptime_percentage >= 80 ? 'warning.main' : 'error.main'}>
                          {carrier.uptime_percentage}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last {carrier.uptime_timeframe_hours}h
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <ProcessedIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">
                            Processed
                          </Typography>
                        </Box>
                        <Typography variant="h6">
                          {carrier.outages_processed ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {carrier.outages_label || 'Outages'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <FrequencyIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">
                            Frequency
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {formatInterval(carrier.configured_interval)}
                        </Typography>
                        {carrier.actual_interval && (
                          <Typography variant="caption" color="text.secondary">
                            Actual: {formatInterval(carrier.actual_interval)}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <ScheduleIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">
                            Next Check
                          </Typography>
                        </Box>
                        <Typography variant="caption">
                          {carrier.next_scheduled_check ? 
                            format(new Date(carrier.next_scheduled_check), 'HH:mm:ss') : 
                            'N/A'
                          }
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Last Activity */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">
                        {carrier.last_success_label || 'Last Success'}: {formatDateTime(carrier.last_successful_connection)}
                      </Typography>
                      {(carrier.id === 'case-mailbox-poller' || carrier.id === 'support-desk-mailbox-poller') && (carrier.total_emails_processed != null || carrier.last_email_processed_at) && (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Emails retrieved: {carrier.total_emails_processed ?? 0}
                            {carrier.last_email_processed_at ? ` · Last email: ${formatDateTime(carrier.last_email_processed_at)}` : ''}
                          </Typography>
                        </>
                      )}
                      {carrier.last_error && (
                        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                          Error: {carrier.last_error}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Last Updated */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {formatDateTime(data.timestamp)}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CarrierStatusPage;
